import React from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  Typography,
  makeStyles,
} from "@material-ui/core";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";

const useStyles = makeStyles((theme) => ({
  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },

  header: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
  },

  content: {
    padding: theme.spacing(4),
  },

  progress: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },

  button: {
    textTransform: "none",
    fontWeight: 600,
  },

  success: {
    color: "#2e7d32",
  },

  error: {
    color: "#d32f2f",
  },
}));

const BulkInvoiceProgress = ({
  rows,
  file,
  selectedAuction,
  externalProgress,
  generating,
  bulkResult,
  onBack,
  onViewHistory,
}) => {
  const classes = useStyles();

  const progress = Math.max(
    0,
    Math.min(100, Number(externalProgress) || 0)
  );

  const isSuccess =
    !generating &&
    bulkResult &&
    bulkResult.S === true;

  const isFailed =
    !generating &&
    bulkResult &&
    bulkResult.S !== true;

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.header}
        title={
          <Typography variant="h6">
            Generate Invoices
          </Typography>
        }
        subheader="STEP 3 — Invoice generation progress"
      />

      <Divider />

      <CardContent className={classes.content}>

        {/* =========================
            GENERATING
        ========================= */}
        {generating && (
          <>
            <Typography variant="body1">
              Generating invoices...
            </Typography>

            <Box className={classes.progress}>
              <LinearProgress
                variant="determinate"
                value={progress}
              />
            </Box>

            <Typography
              variant="body2"
              color="textSecondary"
            >
              {progress}%
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 6 }}
            >
              Please wait...
            </Typography>
          </>
        )}

        {/* =========================
            SUCCESS
        ========================= */}
        {isSuccess && (
          <>
            <Box
              display="flex"
              alignItems="center"
            >
              <CheckCircleIcon
                className={classes.success}
                style={{ marginRight: 8 }}
              />

              <Typography
                variant="h6"
                className={classes.success}
              >
                Invoice generation completed
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography>
                {bulkResult.count ||
                  rows.length}{" "}
                invoice(s) generated successfully.
              </Typography>
            </Box>

            <Box mt={1}>
              <Typography
                variant="body2"
                color="textSecondary"
              >
                File:{" "}
                {file
                  ? file.name
                  : "---"}
              </Typography>

              <Typography
                variant="body2"
                color="textSecondary"
              >
                Auction:{" "}
                {selectedAuction
                  ? selectedAuction.auction_name
                  : "---"}
              </Typography>
            </Box>

            <Box className={classes.actions}>
              <Button
                className={classes.button}
                variant="outlined"
                onClick={onBack}
              >
                Upload Another File
              </Button>

              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                onClick={onViewHistory}
              >
                View Invoice History
              </Button>
            </Box>
          </>
        )}

        {/* =========================
            FAILED
        ========================= */}
        {isFailed && (
          <>
            <Box
              display="flex"
              alignItems="center"
            >
              <ErrorIcon
                className={classes.error}
                style={{ marginRight: 8 }}
              />

              <Typography
                variant="h6"
                className={classes.error}
              >
                Invoice generation failed
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography color="error">
                {bulkResult?.errors ||
                  bulkResult?.message ||
                  "Unable to generate invoices."}
              </Typography>
            </Box>

            <Box className={classes.actions}>
              <Button
                className={classes.button}
                variant="outlined"
                onClick={onBack}
              >
                Back
              </Button>
            </Box>
          </>
        )}

      </CardContent>
    </Card>
  );
};

BulkInvoiceProgress.defaultProps = {
  rows: [],
  file: null,
  selectedAuction: null,
  externalProgress: 0,
  generating: false,
  bulkResult: null,
};

BulkInvoiceProgress.propTypes = {
  rows: PropTypes.array.isRequired,
  file: PropTypes.object,
  selectedAuction: PropTypes.object,
  externalProgress: PropTypes.number,
  generating: PropTypes.bool,
  bulkResult: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
};

export default BulkInvoiceProgress;